import { useState, useEffect } from "react";
import { IconButton, Chip, Checkbox, Button, Tooltip } from "@material-ui/core";
import DownloadIcon from "@mui/icons-material/Download";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import ToolkitTable from "components/Cards/Tables/ToolkitTable";
import {
  columnCNPJCPFField,
  columnNumberField,
  columnDateField,
  columnPriceField,
  columnTextField,
} from "utils/tables-utils";
import checkSituationDays from "./check-situation-days";
import UtilsFncs from "utils/UtilsFncs";
import { LONGTERM_STATUS } from "./table-view";

function isBoletoDownloadAvailable(row) {
  const url = row?.UrlDownloadBoleto;
  return url != null && String(url).trim() !== "";
}

export default function TableFull({
  data,
  onExportBoletos,
  downloadBoleto,
  totalRegistros,
  page: pageProp,
  sizePerPage: sizePerPageProp,
  onTableChange: onTableChangeProp,
}) {
  const [dataTable, setDataTable] = useState([]);
  const defaultPageSize = sizePerPageProp ?? 10;
  const [paginationState, setPaginationState] = useState({
    page: pageProp ?? 1,
    sizePerPage: defaultPageSize,
  });

  const pageSize = paginationState.sizePerPage || 10;
  const currentPage = paginationState.page || 1;
  const pageStart = (currentPage - 1) * pageSize;
  const pageSlice = dataTable?.slice(pageStart, pageStart + pageSize) ?? [];
  const selectableOnPage = pageSlice.filter((item) => !item.disabled);
  const allSelectableOnPageSelected =
    selectableOnPage.length > 0 &&
    selectableOnPage.every((item) => item.selected);

  useEffect(() => {
    setPaginationState((prev) => ({ ...prev, page: 1 }));
  }, [data]);

  useEffect(() => {
    setDataTable(
      data?.map((item, index) => {
        const selectKey =
          item.IdTitulo != null && String(item.IdTitulo).trim() !== ""
            ? String(item.IdTitulo)
            : item.Id != null && String(item.Id).trim() !== ""
              ? String(item.Id)
              : `__row_${index}`;
        return {
          ...item,
          disabled: !isBoletoDownloadAvailable(item),
          selected: false,
          __selectKey: selectKey,
        };
      }),
    );
  }, [data]);

  const handleSelectRow = (key) => {
    setDataTable((prevData) =>
      prevData?.map((item) => {
        if (item.__selectKey !== key || item.disabled) return item;
        return { ...item, selected: !item.selected };
      }),
    );
  };

  const handleSelectAllOnPage = () => {
    const size = paginationState.sizePerPage || 10;
    const p = paginationState.page || 1;
    const start = (p - 1) * size;
    setDataTable((prevData) => {
      const pageRows = prevData?.slice(start, start + size) ?? [];
      const pageKeys = new Set(pageRows.map((row) => row.__selectKey));
      const selectable = pageRows.filter((item) => !item.disabled);
      if (selectable.length === 0) return prevData;
      const allSelectedOnPage = selectable.every((item) => item.selected);
      const shouldSelectAll = !allSelectedOnPage;
      return prevData?.map((item) => {
        if (!pageKeys.has(item.__selectKey)) return item;
        return {
          ...item,
          selected: item.disabled ? false : shouldSelectAll,
        };
      });
    });
  };

  const columnModel = [
    {
      dataField: "selected",
      text: "",
      align: "center",
      headerAlign: "center",
      formatter: (_, row) => (
        <Checkbox
          checked={row.selected}
          onChange={(e) => {
            e.stopPropagation();
            handleSelectRow(row.__selectKey);
          }}
          onClick={(e) => e.stopPropagation()}
          color="primary"
          disabled={row.disabled}
        />
      ),
      headerFormatter: () => (
        <Checkbox
          checked={allSelectableOnPageSelected}
          onChange={handleSelectAllOnPage}
          color="primary"
          disabled={selectableOnPage.length === 0}
        />
      ),
      style: {
        width: "5vw",
      },
    },
    columnTextField({
      dataField: "boleto",
      text: "Boleto",
      align: "center",
      formatter: (_, row) => {
        if (!isBoletoDownloadAvailable(row)) {
          return (
            <Tooltip title="Não disponível">
              <span style={{ display: "inline-flex", cursor: "not-allowed" }}>
                <IconButton disabled size="small" aria-label="Não disponível">
                  <DownloadIcon
                    fontSize="small"
                    style={{ color: "rgba(0, 0, 0, 0.38)" }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          );
        }

        return (
          <Tooltip title="Baixar boleto">
            <span style={{ display: "inline-flex" }}>
              <IconButton
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  downloadBoleto(row.UrlDownloadBoleto);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                size="small"
                style={{ cursor: "pointer" }}
                aria-label="Baixar boleto"
              >
                <DownloadIcon fontSize="small" color="primary" />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    }),
    columnNumberField({
      dataField: "IdTitulo",
      text: "IdTitulo",
      hidden: true,
      csvExport: false,
    }),
    columnNumberField({
      dataField: "NumeroTitulo",
      text: "Nr. Título",
    }),
    columnTextField({
      dataField: "Situacao",
      text: "Status",
      align: "center",
      formatter: (_, row) => {
        const isPaid = row.Situacao === "LQ";
        const isPending = row.Situacao === "AB";
        const isCanceled = row.Situacao === "CA";

        let backgroundColor, borderColor;

        if (isPaid) {
          backgroundColor = "rgba(0, 128, 0, 0.1)";
          borderColor = "1px solid rgba(0, 128, 0, 0.5)";
        } else if (isPending) {
          backgroundColor = "rgba(255, 193, 7, 0.1)";
          borderColor = "1px solid rgba(255, 193, 7, 0.5)";
        } else if (isCanceled) {
          backgroundColor = "rgba(244, 67, 54, 0.1)";
          borderColor = "1px solid rgba(244, 67, 54, 0.5)";
        } else {
          backgroundColor = "rgba(128, 128, 128, 0.1)";
          borderColor = "1px solid rgba(128, 128, 128, 0.5)";
        }

        return (
          <Chip
            label={LONGTERM_STATUS.get(row.Situacao)}
            size="small"
            style={{
              width: "100px",
              padding: "1rem 0.5rem",
              backgroundColor: backgroundColor,
              border: borderColor,
            }}
          />
        );
      },
    }),
    columnDateField({
      dataField: "DataEmissao",
      text: "Emissão",
    }),
    columnDateField({
      dataField: "DtVencimento",
      text: "Vencimento",
    }),
    columnDateField({
      dataField: "DtVencimentoProrrogado",
      text: "Vencimento Prorrogado",
    }),
    columnDateField({
      dataField: "DtLiquidacao",
      text: "Data Pagamento",
    }),
    columnPriceField({
      dataField: "Valor",
      text: "Valor",
    }),
    columnPriceField({
      dataField: "ValorEmAberto",
      text: "Valor em Aberto",
    }),
    columnTextField({
      dataField: "CCLoja",
      text: "CC Loja",
      align: "center",
      headerAlign: "center",
    }),
    columnTextField({
      dataField: "MarcaDoPedido",
      text: "Marca do Pedido",
    }),
    columnTextField({
      dataField: "CodERP",
      text: "Código ERP",
    }),
    columnTextField({
      dataField: "RazaoSocial",
      text: "Razão Social",
    }),
    columnCNPJCPFField({
      dataField: "Cnpj",
      text: "CNPJ",
    }),
    columnTextField({
      dataField: "Loja",
      text: "Loja",
    }),
    columnTextField({
      dataField: "SituacaoDias",
      text: "Situação (Dias)",
      formatter: (row, cell) => checkSituationDays(cell),
    }),
    columnTextField({
      dataField: "ValorTotalNf",
      text: "Valor Total NF",
      formatter: (cell) => {
        return cell !== null ? `R$ ${cell}` : "R$ 0,00";
      },
    }),
    columnNumberField({
      dataField: "NumeroNf",
      text: "Nr. NF",
    }),
    columnTextField({
      dataField: "Observacao",
      text: "Observação",
      headerAlign: "center",
      align: "center",
    }),
    columnNumberField({
      dataField: "NumeroTituloBanco",
      text: "Nr. Título Banco",
    }),
    columnTextField({
      dataField: "LinhaDigitavelBanco",
      text: "Linha Digitável Banco",
      headerAlign: "center",
      align: "center",
      formatter: (_, row) => {
        return (
          <IconButton
            color="primary"
            onClick={() => UtilsFncs.CopyToClipboard(row.LinhaDigitavelBanco)}
          >
            <FileCopyIcon fontSize="small" color="textPrimary" />
          </IconButton>
        );
      },
    }),
    columnTextField({
      dataField: "NumeroPedido",
      text: "Nr. Pedido",
      formatter: (value) => (value !== 0 && value !== "0" ? value : ""),
    }),
    columnTextField({
      dataField: "NumeroProposta",
      text: "Nr. Proposta",
      formatter: (value) => value?.replace(/["]/g, ""),
    }),
    columnTextField({
      dataField: "CondicaoPgto",
      text: "Condição Pagamento",
    }),
    columnTextField({
      dataField: "CodPortador",
      text: "Código Portador",
    }),
  ];

  const syncPaginationFromTable = (page, sizePerPage) => {
    setPaginationState((prev) => ({
      page,
      sizePerPage:
        sizePerPage != null && !Number.isNaN(Number(sizePerPage))
          ? Number(sizePerPage)
          : prev.sizePerPage,
    }));
  };

  return (
    <ToolkitTable
      dataTable={dataTable}
      keyField="__selectKey"
      headerTitle="Títulos"
      columnModel={columnModel}
      pagination={{
        options: [10, 25, 50, 100],
        defaultOption: paginationState.sizePerPage,
        totalSize: totalRegistros ?? dataTable?.length ?? 0,
        page: paginationState.page,
        sizePerPage: paginationState.sizePerPage,
        showTotal: true,
        onPageChange: (newPage, newSizePerPage) => {
          syncPaginationFromTable(newPage, newSizePerPage);
          onTableChangeProp?.("pagination", {
            page: newPage,
            sizePerPage: newSizePerPage,
          });
        },
        onSizePerPageChange: (newSizePerPage, newPage) => {
          syncPaginationFromTable(
            newPage,
            newSizePerPage != null ? newSizePerPage : undefined,
          );
          onTableChangeProp?.("pagination", {
            page: newPage,
            sizePerPage: newSizePerPage,
          });
        },
      }}
      notFoundTitle="Sua busca não encontrou nenhum resultado correspondente."
      showPrint
      showExport
      csvFileName="RelatorioAcompanhamentoTitulos.csv"
      extraActions={
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            const selectedRows = dataTable?.filter((item) => item.selected);
            if (onExportBoletos && selectedRows?.length > 0) {
              onExportBoletos(selectedRows);
            }
          }}
          disabled={!dataTable?.some((item) => item.selected)}
        >
          Exportar boletos
        </Button>
      }
    />
  );
}
